'use client'

import { useMemo, useState } from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Checkbox,
  Grid,
  HStack,
  Input,
  Spinner,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { AppCard, AppField, CustomSelect } from '@/shared'
import {
  useGetDonorCardDetailQuery,
  useGetDonorDetailQuery,
} from '@/entities/donor/api/get-donor.api'
import { useGetVisitExaminationsQuery } from '@/entities/visit/api'
import { ExaminationStatusBadge } from '@/entities/visit/ui'
import { MedicalDeferralModal } from '@/features/medical-deferral'
import { useMedicalHistoryForm } from '../model/use-medical-history-form'
import {
  hasActiveStep,
  isDonorCardReadOnly,
} from '../model/use-donor-card-read-only'

// ─── Component ────────────────────────────────────────────────────────────────

export default function MedicalHistoryTab({ visitId }: { visitId: string }) {
  const { t } = useTranslation()
  const [isDeferralModalOpen, setIsDeferralModalOpen] = useState(false)

  const { data: donor } = useGetDonorDetailQuery(visitId)
  const { data: donorCard } = useGetDonorCardDetailQuery(donor?.donor_id ?? '')
  const { data: examinations = [] } = useGetVisitExaminationsQuery(visitId)

  const { form, isPending, onSubmit, needsAmbulatoryExam } =
    useMedicalHistoryForm({
      visitId,
      donorId: donor?.donor_id ?? '',
      donorCard,
    })

  const {
    register,
    control,
    trigger,
    formState: { errors },
  } = form

  const handleOpenDeferralModal = async () => {
    const isValid = await trigger()
    if (isValid) {
      setIsDeferralModalOpen(true)
    }
  }

  // The exam form is the active step's action — never on a read-only card.
  const showExamForm =
    hasActiveStep(donorCard, 'EXAMINATION') && !isDonorCardReadOnly(donorCard)

  const formatExaminedAt = (iso: string) => {
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return iso
    return date.toLocaleDateString('ru-RU')
  }

  const orgOptions = useMemo(
    () => [
      { label: t('donor_detail.medical_history.org_rkb'), value: 'rkb' },
      { label: t('donor_detail.medical_history.org_center'), value: 'center' },
      { label: t('donor_detail.medical_history.org_clinic'), value: 'clinic' },
    ],
    [t],
  )

  const columns = [
    t('donor_detail.medical_history.col_date'),
    t('donor_detail.medical_history.col_doctor'),
    t('donor_detail.medical_history.col_conclusion'),
    t('donor_detail.medical_history.col_status'),
  ]

  return (
    <VStack gap={5} align="stretch">
      {/* ── Therapist exam form ──────────────────────────────────── */}
      {showExamForm && (
        <AppCard.Root>
          <AppCard.Body p={5}>
            <Text fontWeight="semibold" fontSize="sm" mb={4}>
              {t('donor_detail.medical_history.exam_title')}
            </Text>

            <form onSubmit={onSubmit}>
              <VStack gap={4} align="stretch">
                <Grid
                  templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
                  gap={4}
                >
                  <AppField.Root invalid={!!errors.height_cm}>
                    <AppField.Label fontSize="sm">
                      {t('donor_detail.stats.height')}
                    </AppField.Label>
                    <HStack gap={2}>
                      <Input
                        type="number"
                        size="sm"
                        bg="bg.subtle"
                        borderColor="border"
                        borderRadius="md"
                        {...register('height_cm')}
                      />
                      <Text fontSize="sm" color="fg.muted" flexShrink={0}>
                        {t('donor_detail.stats.cm')}
                      </Text>
                    </HStack>
                    <AppField.ErrorText>
                      {errors.height_cm?.message}
                    </AppField.ErrorText>
                  </AppField.Root>

                  <AppField.Root invalid={!!errors.weight_kg}>
                    <AppField.Label fontSize="sm">
                      {t('donor_detail.stats.weight')}
                    </AppField.Label>
                    <HStack gap={2}>
                      <Input
                        type="number"
                        size="sm"
                        bg="bg.subtle"
                        borderColor="border"
                        borderRadius="md"
                        {...register('weight_kg')}
                      />
                      <Text fontSize="sm" color="fg.muted" flexShrink={0}>
                        {t('donor_detail.stats.kg')}
                      </Text>
                    </HStack>
                    <AppField.ErrorText>
                      {errors.weight_kg?.message}
                    </AppField.ErrorText>
                  </AppField.Root>

                  <AppField.Root
                    invalid={
                      !!errors.blood_pressure_systolic ||
                      !!errors.blood_pressure_diastolic
                    }
                  >
                    <AppField.Label fontSize="sm">
                      {t('donor_detail.stats.pressure')}
                    </AppField.Label>
                    <HStack gap={2}>
                      <Input
                        type="number"
                        size="sm"
                        bg="bg.subtle"
                        borderColor="border"
                        borderRadius="md"
                        {...register('blood_pressure_systolic')}
                      />
                      <Text fontSize="sm" color="fg.muted">
                        /
                      </Text>
                      <Input
                        type="number"
                        size="sm"
                        bg="bg.subtle"
                        borderColor="border"
                        borderRadius="md"
                        {...register('blood_pressure_diastolic')}
                      />
                      <Text fontSize="sm" color="fg.muted" flexShrink={0}>
                        {t('donor_detail.stats.mmhg')}
                      </Text>
                    </HStack>
                    <AppField.ErrorText>
                      {errors.blood_pressure_systolic?.message ??
                        errors.blood_pressure_diastolic?.message}
                    </AppField.ErrorText>
                  </AppField.Root>

                  <AppField.Root invalid={!!errors.pulse}>
                    <AppField.Label fontSize="sm">
                      {t('donor_detail.stats.pulse')}
                    </AppField.Label>
                    <HStack gap={2}>
                      <Input
                        type="number"
                        size="sm"
                        bg="bg.subtle"
                        borderColor="border"
                        borderRadius="md"
                        {...register('pulse')}
                      />
                      <Text fontSize="sm" color="fg.muted" flexShrink={0}>
                        {t('donor_detail.stats.bpm')}
                      </Text>
                    </HStack>
                    <AppField.ErrorText>
                      {errors.pulse?.message}
                    </AppField.ErrorText>
                  </AppField.Root>

                  <AppField.Root invalid={!!errors.temperature_c}>
                    <AppField.Label fontSize="sm">
                      {t('donor_detail.stats.temperature')}
                    </AppField.Label>
                    <HStack gap={2}>
                      <Input
                        type="number"
                        step="0.1"
                        size="sm"
                        bg="bg.subtle"
                        borderColor="border"
                        borderRadius="md"
                        {...register('temperature_c')}
                      />
                      <Text fontSize="sm" color="fg.muted" flexShrink={0}>
                        {t('donor_detail.stats.celsius')}
                      </Text>
                    </HStack>
                    <AppField.ErrorText>
                      {errors.temperature_c?.message}
                    </AppField.ErrorText>
                  </AppField.Root>
                </Grid>

                <AppField.Root invalid={!!errors.conclusion}>
                  <AppField.Label fontSize="sm">
                    {t('donor_detail.medical_history.complaints_label')}
                  </AppField.Label>
                  <Textarea
                    placeholder={t(
                      'donor_detail.medical_history.complaints_placeholder',
                    )}
                    rows={4}
                    bg="bg.subtle"
                    borderColor="border"
                    borderRadius="md"
                    resize="vertical"
                    fontSize="sm"
                    {...register('conclusion')}
                  />
                  <AppField.ErrorText>
                    {errors.conclusion?.message}
                  </AppField.ErrorText>
                </AppField.Root>

                <Controller
                  control={control}
                  name="needs_ambulatory_exam"
                  render={({ field }) => (
                    <Checkbox.Root
                      checked={field.value}
                      onCheckedChange={({ checked }) =>
                        field.onChange(checked === true)
                      }
                      onBlur={field.onBlur}
                      colorPalette="red"
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control
                        borderColor="red.500"
                        cursor="pointer"
                      />
                      <Checkbox.Label fontSize="sm">
                        {t('donor_detail.medical_history.ambulatory_checkbox')}
                      </Checkbox.Label>
                    </Checkbox.Root>
                  )}
                />

                <AppField.Root>
                  <AppField.Label fontSize="sm">
                    {t('donor_detail.medical_history.referred_to_label')}
                  </AppField.Label>
                  <Controller
                    control={control}
                    name="referral_organization_id"
                    render={({ field }) => (
                      <CustomSelect
                        width="100%"
                        borderRadius="md"
                        bg="bg.surface"
                        borderColor="border"
                        size="sm"
                        options={orgOptions}
                        disabled={!needsAmbulatoryExam}
                        value={field.value ? [field.value] : []}
                        onChange={(value) => field.onChange(value[0] ?? '')}
                      />
                    )}
                  />
                </AppField.Root>

                <HStack justify="flex-end" gap={3}>
                  <Button
                    type="button"
                    color="primary.fg"
                    borderColor="primary.fg"
                    borderRadius="md"
                    size="sm"
                    px={6}
                    variant="outline"
                    _hover={{ opacity: 0.85 }}
                    _active={{ opacity: 0.7 }}
                    onClick={handleOpenDeferralModal}
                  >
                    {t('donor_detail.medical_history.med_rejection')}
                  </Button>
                  <Button
                    type="submit"
                    bg="primary.solid"
                    color="white"
                    borderRadius="md"
                    size="sm"
                    px={6}
                    _hover={{ opacity: 0.85 }}
                    _active={{ opacity: 0.7 }}
                    disabled={isPending || !donor?.donor_id}
                  >
                    {isPending ? (
                      <Spinner size="xs" color="white" />
                    ) : (
                      t('donor_detail.medical_history.save')
                    )}
                  </Button>
                </HStack>
              </VStack>
            </form>
          </AppCard.Body>
        </AppCard.Root>
      )}

      {/* )} */}

      <MedicalDeferralModal
        open={isDeferralModalOpen}
        visitId={visitId}
        donorId={donor?.donor_id ?? ''}
        examinationForm={form}
        onClose={() => setIsDeferralModalOpen(false)}
      />

      {/* ── Exam history table ───────────────────────────────────── */}

      <AppCard.Root>
        <AppCard.Body p={5}>
          <Text fontWeight="semibold" fontSize="sm" mb={4}>
            {t('donor_detail.medical_history.history_title')}
          </Text>

          <Grid
            templateColumns="90px 140px 3fr 1fr"
            gap={2}
            pb={2}
            borderBottomWidth="1px"
            borderColor="border"
          >
            {columns.map((col) => (
              <Text
                key={col}
                fontSize="xs"
                color="fg.muted"
                fontWeight="semibold"
              >
                {col}
              </Text>
            ))}
          </Grid>

          <VStack gap={0} align="stretch">
            {examinations.map((row) => (
              <Grid
                key={`${row.visit_id}-${row.examined_at}`}
                templateColumns="90px 140px 3fr 1fr"
                gap={2}
                py={3}
                borderBottomWidth="1px"
                borderColor="border.muted"
                _last={{ borderBottom: 'none' }}
                alignItems="center"
              >
                <Text fontSize="sm" color="fg.muted">
                  {formatExaminedAt(row.examined_at)}
                </Text>
                <Text fontSize="sm">{row.doctor}</Text>
                <Text fontSize="sm" color="fg.muted" truncate>
                  {row.conclusion}
                </Text>
                <ExaminationStatusBadge status={row.status}>
                  {row.status_label}
                </ExaminationStatusBadge>
              </Grid>
            ))}
          </VStack>
        </AppCard.Body>
      </AppCard.Root>
    </VStack>
  )
}
